import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import DocumentSelector from "./DocumentSelector";
import ErrorPopup from "../../utils/popup/ErrorPopup";

// A dedicated wrapper for the embedded document selector to provide a consistent header.
const EmbeddedDocumentSelector = ({
  fabricTreeData,
  selectedDocIds,
  onDocumentSelect,
  selectAllDocuments,
  deselectAllDocuments,
}) => {
  const totalDocumentCount =
    fabricTreeData?.[0]?.threads?.reduce(
      (count, thread) => count + (thread.documents?.length || 0),
      0
    ) || 0;

  if (!fabricTreeData || fabricTreeData.length === 0) {
    return (
      <div className="ev-card-2">
        <div className="text-center">
          <i className="bi bi-hourglass-split ev-reg-p-blue"></i>
          <p className="ev-small-p-blue mt-2">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-100">
      <div className="d-flex justify-content-between align-items-center mt-5 mb-2">
        <div>
          <h6 className="ev-reg-p-blue ev-bold-med mb-0">Select Data for AI</h6>
        </div>
      </div>

      <div
        className="ev-card-3 ev-radius-small p-0"
      >
        <DocumentSelector
          isEmbedded={true}
          fabricTreeData={fabricTreeData}
          selectedDocIds={selectedDocIds}
          onDocumentSelect={onDocumentSelect}
          hideViewToggle={true}
        />
      </div>
    </div>
  );
};

const ConfigureVM = ({ initialConfig, fabricTreeData, onSave, onClose }) => {
  const [stakeholderRequirement, setStakeholderRequirement] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newSubtypes, setNewSubtypes] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState("");

  const parseCategorySubtypeMapping = (categoryStr, subtypesArray) => {
    if (!categoryStr || !subtypesArray || !Array.isArray(subtypesArray))
      return [];

    const categoryMappings = categoryStr
      .split(",")
      .map((mapping) => {
        const [name, countStr] = mapping.split(":").map((s) => s.trim());
        return { name, count: parseInt(countStr) || 0 };
      })
      .filter((mapping) => mapping.name);

    let subtypeIndex = 0;
    return categoryMappings.map((mapping, index) => {
      const categorySubtypes = subtypesArray.slice(
        subtypeIndex,
        subtypeIndex + mapping.count
      );
      subtypeIndex += mapping.count;
      return {
        id: `parsed_${Date.now()}_${index}`,
        name: mapping.name,
        subtypes: categorySubtypes,
        isEditing: false,
        originalName: mapping.name,
        originalSubtypes: [...categorySubtypes],
      };
    });
  };

  const serializeCategoriesToDbFormat = (categoriesArray) => {
    const typeNameParts = [];
    const allSubtypes = [];

    categoriesArray.forEach((category) => {
      typeNameParts.push(`${category.name}:${category.subtypes.length}`);
      allSubtypes.push(...category.subtypes);
    });

    return {
      type_name: typeNameParts.join(","),
      sub_types: allSubtypes.join(","),
    };
  };

  // In ConfigureVM.jsx, add this useEffect to reset selected documents when fabric changes
  useEffect(() => {
    // When fabric changes, reset selected documents to match the new fabric
    if (initialConfig && fabricTreeData && fabricTreeData.length > 0) {
      // Check if the current selected documents exist in the new fabric
      const allDocIds = fabricTreeData[0]?.threads?.flatMap(
        thread => thread.documents?.map(doc => doc.id) || []
      ) || [];

      // Filter selected documents to only include those that exist in the new fabric
      const validSelectedDocIds = (initialConfig.selected_doc_ids || []).filter(
        docId => allDocIds.includes(docId)
      );

      // Update selected documents to only include valid ones
      setSelectedDocIds(validSelectedDocIds);
    }
  }, [fabricTreeData, initialConfig]);

  useEffect(() => {
    if (initialConfig) {
      setStakeholderRequirement(initialConfig.stakeholder_requirement || "");
      setSelectedDocIds(initialConfig.selected_doc_ids || []);

      let initialCategories = [];

      if (initialConfig.categories && Array.isArray(initialConfig.categories)) {
        initialCategories = initialConfig.categories.map((cat) => ({
          ...cat,
          id: cat.id || `temp_${Date.now()}_${Math.random()}`,
          isEditing: false,
          originalName: cat.name,
          originalSubtypes: [...(cat.subtypes || [])],
        }));
      } else if (initialConfig.category && initialConfig.subcategory) {
        initialCategories = parseCategorySubtypeMapping(
          initialConfig.category,
          initialConfig.subcategory
        );
      } else if (initialConfig.type_name && initialConfig.sub_types) {
        const subtypes =
          typeof initialConfig.sub_types === "string"
            ? initialConfig.sub_types
              .split(",")
              .map((name) => name.trim())
              .filter(Boolean)
            : initialConfig.sub_types;
        initialCategories = parseCategorySubtypeMapping(
          initialConfig.type_name,
          subtypes
        );
      }

      setCategories(initialCategories);
    }
  }, [initialConfig]);

  const closeErrorPopup = () => setIsErrorPopupVisible(false);

  const isDuplicateCategoryName = (name, excludeId = null) => {
    const trimmedName = name.trim().toLowerCase();
    return categories.some(
      (cat) =>
        cat.id !== excludeId && cat.name.trim().toLowerCase() === trimmedName
    );
  };

  const handleAddCategory = () => {
    const trimmedName = newCategoryName.trim();
    if (!trimmedName) {
      setPopupErrorMessage("Category name is required to add a new category.");
      setIsErrorPopupVisible(true);
      return;
    }
    if (isDuplicateCategoryName(trimmedName)) {
      setPopupErrorMessage(`Category name '${trimmedName}' already exists.`);
      setIsErrorPopupVisible(true);
      return;
    }

    const subtypesArray = newSubtypes.trim()
      ? newSubtypes
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
      : [];
    const newCategory = {
      id: `new_${Date.now()}`,
      name: trimmedName,
      subtypes: subtypesArray,
      isEditing: false,
      originalName: trimmedName,
      originalSubtypes: [...subtypesArray],
    };

    setCategories((prev) => [newCategory, ...prev]);
    setNewCategoryName("");
    setNewSubtypes("");
  };

  const handleDiscardCategory = (categoryId) => {
    setCategories((prev) => prev.filter((c) => c.id !== categoryId));
  };

  const toggleCategoryEdit = (categoryId) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? { ...cat, isEditing: !cat.isEditing }
          : { ...cat, isEditing: false }
      )
    );
  };

  const updateCategoryField = (categoryId, field, value) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId ? { ...cat, [field]: value } : cat
      )
    );
  };

  const saveCategoryEdit = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    if (!category) return;

    const newName = category.name.trim();
    if (!newName) {
      setPopupErrorMessage("Category name cannot be empty.");
      setIsErrorPopupVisible(true);
      return;
    }

    if (isDuplicateCategoryName(newName, categoryId)) {
      setPopupErrorMessage(`Category name '${newName}' already exists.`);
      setIsErrorPopupVisible(true);
      return;
    }

    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.id === categoryId) {
          const updatedSubtypes = Array.isArray(category.subtypes)
            ? category.subtypes
            : (category.subtypes || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
          return {
            ...cat,
            isEditing: false,
            name: newName,
            subtypes: updatedSubtypes,
            originalName: newName,
            originalSubtypes: [...updatedSubtypes],
          };
        }
        return cat;
      })
    );
  };

  const cancelCategoryEdit = (categoryId) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
            ...cat,
            isEditing: false,
            name: cat.originalName,
            subtypes: [...cat.originalSubtypes],
          }
          : cat
      )
    );
  };

  const handleDocumentSelection = (docIds) => {
    setSelectedDocIds(docIds);
  };

  const handleSave = () => {
    if (!stakeholderRequirement.trim()) {
      setPopupErrorMessage("Stakeholder Requirement cannot be empty.");
      setIsErrorPopupVisible(true);
      return;
    }

    const finalCategories = categories.map(({ id, name, subtypes }) => ({
      id,
      name,
      subtypes,
    }));
    const dbFormat = serializeCategoriesToDbFormat(categories);

    const finalConfig = {
      stakeholder_requirement: stakeholderRequirement,
      categories: finalCategories,
      type_name: dbFormat.type_name,
      sub_types: dbFormat.sub_types,
      selected_doc_ids: selectedDocIds,
    };

    onSave(finalConfig);
  };

  const selectAllDocuments = () => {
    const allDocIds =
      fabricTreeData?.[0]?.threads?.flatMap(
        (thread) => thread.documents?.map((doc) => doc.id) || []
      ) || [];
    setSelectedDocIds(allDocIds);
  };

  const deselectAllDocuments = () => {
    setSelectedDocIds([]);
  };

  const renderCategoryRow = (category) => (
    <div
      key={category.id}
      className="row mb-2 align-items-center ev-card-3 p-2 mx-1 ev-radius-small"
    >
      {category.isEditing ? (
        <>
          <div className="col-4">
            <input
              type="text"
              className="formField4 ev-btn-small ev-radius-xsmall ms-0"
              value={category.name}
              onChange={(e) =>
                updateCategoryField(category.id, "name", e.target.value)
              }
              placeholder="Category name"
            />
          </div>
          <div className="col-5">
            <input
              type="text"
              className="formField4 ev-btn-small ev-radius-xsmall"
              value={
                Array.isArray(category.subtypes)
                  ? category.subtypes.join(", ")
                  : category.subtypes
              }
              onChange={(e) =>
                updateCategoryField(category.id, "subtypes", e.target.value)
              }
              placeholder="Subtypes (comma separated)"
            />
          </div>
          <div className="col-3 d-flex justify-content-end">
            <button
              className="ev-btn-lightblue-fill-round me-1 px-2 py-1"
              onClick={() => cancelCategoryEdit(category.id)}
            >
              <i className="bi bi-x-circle"></i>
            </button>
            <button
              className="ev-btn-blue-fill-round me-1 px-2 py-1"
              onClick={() => saveCategoryEdit(category.id)}
            >
              <i className="bi bi-check-circle"></i>
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="col-4">
            <strong className="ev-small-p-blue">{category.name}</strong>
          </div>
          <div className="col-5">
            <div className="d-flex flex-wrap gap-1">
              {category.subtypes && category.subtypes.length > 0 ? (
                category.subtypes.map((subtype, index) => (
                  <span key={index} className="ev-message-card2 ev-btn-xsmall">
                    {subtype}
                  </span>
                ))
              ) : (
                <em className="ev-small-p-dark">No subtypes</em>
              )}
            </div>
          </div>
          <div className="col-3 d-flex justify-content-end gap-1">
            <button
              className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg"
              onClick={() => toggleCategoryEdit(category.id)}
            >
              <i className="bi bi-pencil"></i> Edit
            </button>
            <button
              className="ev-btn-red-fill-round ev-btn-small ev-radius-xsmall ev-bold-reg px-2 py-1  ms-1"
              onClick={() => handleDiscardCategory(category.id)}
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      {createPortal(
        <div
          className="modal fade show d-block bg-dark bg-opacity-50"
          style={{ zIndex: 'var(--num-100000000)' }}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
          >
            <div className="modal-content ev-message-card2 d-block ev-card-2 ev-padding-large" >
              {/* Two-Panel Body Layout */}
              <div className="d-flex gap-3">
                {/* Left Panel - Configuration Form */}
                <div style={{ flex: '0 0 45%', minWidth: 0 }}>
                  <h5 className="ev-med-p-blue mb-4">Configure Value Message</h5>
                  <div className="mb-3">
                    <label className="form-label ev-reg-p-dark mt-2">
                      Stakeholder Requirement &nbsp;
                      <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="formField3"
                      rows="3"
                      placeholder="Enter requirement"
                      value={stakeholderRequirement}
                      onChange={(e) =>
                        setStakeholderRequirement(e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="form-label ev-reg-p-dark">
                      Value Message Categories
                    </label>
                    <div className="ev-card-8 p-2 ev-radius-small mb-3">
                      <div className="row g-2 mb-0 align-items-end">
                        <div className="col-5">
                          <label className="form-label ev-small-p-dark">
                            New Category
                          </label>
                          <input
                            type="text"
                            className="formField4 ev-btn-reg"
                            placeholder="Category Name"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                          />
                        </div>
                        <div className="col-5">
                          <label className="form-label ev-small-p-dark">
                            Subtypes <i>(comma-separated)</i>
                          </label>
                          <input
                            type="text"
                            className="formField4 ev-btn-reg"
                            placeholder="Subtype A, Subtype B"
                            value={newSubtypes}
                            onChange={(e) => setNewSubtypes(e.target.value)}
                          />
                        </div>
                        <div className="col-2">
                          <button
                            type="button"
                            className="ev-btn-lightblue-fill-round ev-bold-reg w-100 text-start"
                            onClick={handleAddCategory}
                          >
                            <i className="bi bi-plus-circle"></i>  Add
                          </button>
                        </div>
                      </div>
                    </div>
                    {categories.length > 0 && (
                      <>
                        <label className="form-label ev-reg-p-dark">
                          Added Categories
                        </label>
                        <div className="scrollable-element vh-25">
                          {categories.map(renderCategoryRow)}
                        </div>
                      </>
                    )}

                  </div>
                </div>

                {/* Right Panel - Document Selector (NO BORDER) */}
                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                  <EmbeddedDocumentSelector
                    fabricTreeData={fabricTreeData}
                    selectedDocIds={selectedDocIds}
                    onDocumentSelect={handleDocumentSelection}
                    selectAllDocuments={selectAllDocuments}
                    deselectAllDocuments={deselectAllDocuments}
                  />
                </div>
              </div>

              {/* Custom Footer (NO BORDER) */}
              <div className="p-3 d-flex justify-content-end align-items-center gap-2">
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-bold-reg"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ev-btn-blue-fill-round ev-bold-reg"

                  onClick={handleSave}
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      { isErrorPopupVisible && (
          <ErrorPopup
            onClose={closeErrorPopup}
            message={popupErrorMessage}
          />
        )}
    </>
  );
};

export default ConfigureVM;